import Close from '@mui/icons-material/Close';
import {
  alpha,
  Box,
  Drawer,
  IconButton,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import IncentivioDots from 'components/incentivio-logo/incentivio-dots.component';
import WorkspaceSelector from 'components/workspace-selector/workspace-selector.component';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_TRANSITION,
} from './sidebar.constants';
import { SidebarContext } from './sidebar.context';
import { useSidebarComponent } from './sidebar.hooks';
import SidebarList from './sidebar-list/sidebar-list.component';
import SidebarLogo from './sidebar-logo/sidebar-logo.component';
import SidebarPinButton from './sidebar-pin-button/sidebar-pin-button.component';

const Sidebar = ({ routeConfig, open, onClose }) => {
  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const { pinned, expanded, onMouseEnter, onMouseLeave, onTogglePin } =
    useSidebarComponent();

  const navWidth = pinned ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
  const paperWidth = expanded
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  // Navy -> purple gradient surface; all content is recolored white for contrast.
  const white = theme.palette.common.white;
  const sidebarPaperSx = {
    background: `linear-gradient(178deg, ${theme.palette.sidebar.gradientTop} 0%, ${theme.palette.sidebar.gradientBottom} 92%)`,
    color: white,
    '& .MuiListItemIcon-root': {
      color: alpha(white, 0.82),
    },
    '& .MuiListItemButton-root:hover': {
      backgroundColor: alpha(white, 0.12),
    },
    '& .MuiListItemButton-root.Mui-selected': {
      backgroundColor: alpha(white, 0.2),
      color: white,
      fontWeight: 600,
    },
    '& .MuiListItemButton-root.Mui-selected:hover': {
      backgroundColor: alpha(white, 0.28),
    },
    '& .MuiListItemButton-root.Mui-selected .MuiListItemIcon-root': {
      color: white,
    },
    '& .MuiListSubheader-root': {
      color: alpha(white, 0.6),
    },
    '& .MuiDivider-root': {
      borderColor: alpha(white, 0.18),
    },
    '& *::-webkit-scrollbar': {
      width: 6,
    },
    '& *::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(white, 0.18),
      borderRadius: 3,
    },
  };

  return matchesSm ? (
    <Box
      component="nav"
      sx={{
        width: navWidth,
        flexShrink: 0,
      }}
      aria-label={t('Sidebar.ariaLabel')}
      data-testid="Sidebar.Nav"
    >
      <Drawer
        variant="permanent"
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: paperWidth,
            p: 1,
            pt: 0,
            border: 'none',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            transition: SIDEBAR_TRANSITION,
            ...sidebarPaperSx,
          },
        }}
        open
        PaperProps={{
          // @ts-ignore
          'data-testid': 'Sidebar.Drawer',
          elevation: 16,
          onMouseEnter,
          onMouseLeave,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            color: white,
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            px: expanded ? 0 : 1,
          }}
        >
          {expanded ? (
            <>
              <Box sx={{ mr: 1, height: '40px' }}>
                <SidebarLogo />
              </Box>
              <SidebarPinButton pinned={pinned} onTogglePin={onTogglePin} />
            </>
          ) : (
            <Box sx={{ height: '40px' }}>
              <MuiLink
                component={Link}
                underline="none"
                to="/"
                sx={{ display: 'flex', height: '40px', width: '40px' }}
                data-testid="Sidebar.CollapsedLogo"
              >
                <IncentivioDots color={white} />
              </MuiLink>
            </Box>
          )}
        </Box>
        <SidebarContext.Provider value={{ expanded }}>
          <Box
            sx={{
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            <SidebarList routeConfig={routeConfig} onClose={onClose} />
          </Box>
        </SidebarContext.Provider>
      </Drawer>
    </Box>
  ) : (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { xs: 1, sm: 600 },
          p: 1,
          ...sidebarPaperSx,
        },
      }}
      PaperProps={{
        // @ts-ignore
        'data-testid': 'Sidebar.Drawer',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          color: white,
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ mr: 1, height: '40px' }}>
            <SidebarLogo />
          </Box>
          <Box sx={{ position: 'absolute', right: 0 }}>
            <IconButton
              data-testid="Sidebar.IconButton.Close"
              onClick={onClose}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <WorkspaceSelector inSidebar />
      <Box
        sx={{
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <SidebarList routeConfig={routeConfig} onClose={onClose} />
      </Box>
    </Drawer>
  );
};

export default Sidebar;
